"use client";

import type { PanInfo } from "framer-motion";
import { useCallback, useRef, useState } from "react";
import type { RollWidgetAnchor } from "./roll-widget-slots";

/**
 * Glisser un widget vers un autre coin du roll, où il se range à la lâcher.
 *
 * Le coin se choisit sur la moitié du calque où le pointeur est lâché. Le haut
 * au centre n'en fait pas partie : c'est la place de la capsule du parcours.
 *
 * À poser sur un `motion` avec `drag`, `dragSnapToOrigin` et `onDragEnd` : le
 * widget revient à son origine pendant qu'il change d'ancre, donc rien ne reste
 * décalé dans le nouveau coin.
 *
 * **`fallback` est suivi tant que personne n'a glissé le widget.** Ce fut une
 * valeur initiale, retenue au montage ; elle ne pouvait alors pas dépendre de
 * l'écran, qui change de forme sous nos pieds. Les caméras d'un replay se
 * posent en bas sur un téléphone couché et en haut sur le même téléphone
 * debout — le bas, debout, c'est là que tombent les notes —, et une valeur
 * figée au montage aurait décrit l'orientation d'entrée. Le premier glissement
 * fige le choix : déplacer une vignette, puis tourner l'appareil, ne la reprend
 * pas des mains.
 */
export function useCornerDrag(fallback: RollWidgetAnchor) {
  const [chosen, setChosen] = useState<RollWidgetAnchor | null>(null);
  const anchor = chosen ?? fallback;
  const boxRef = useRef<HTMLDivElement | null>(null);

  const onDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const layer = boxRef.current?.closest("[data-roll-widget-layer]");

      if (!layer) {
        return;
      }

      const rect = layer.getBoundingClientRect();
      const x = info.point.x - window.scrollX;
      const y = info.point.y - window.scrollY;
      const vertical = y < rect.top + rect.height / 2 ? "top" : "bottom";
      const horizontal = x < rect.left + rect.width / 2 ? "left" : "right";

      setChosen(`${vertical}-${horizontal}`);
    },
    [],
  );

  return { anchor, boxRef, onDragEnd };
}
